import React, { ReactElement, useEffect, useState } from 'react';
import { fromDom } from 'hast-util-from-dom';
import { toH } from 'hast-to-hyperscript';
import isEmpty from 'lodash/isEmpty';
import { useCallbackRef, useDangerousRawHtml } from '../../hooks/useCallbackRef';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  html?: string;
  isModal?: boolean;
  loadDefer?: boolean;
};

export const postScript = () => {
  // GetCookies function to debug the script
  const getCookies = () => {
    const pairs = document.cookie.split(';');
    const cookies = {};
    for (let i = 0; i < pairs.length; i++) {
      const cookie = pairs[i].split('=');
      cookies[(cookie[0] + '').trim()] = unescape(cookie.slice(1).join('='));
    }
    return cookies;
  };
  const getCookiesByName = (elementName) => {
    const msuuidElement = document.cookie.split(';').find((ele) => ele.includes(elementName));
    return msuuidElement && msuuidElement.split('=')[1] ? msuuidElement.split('=')[1] : '';
  };

  const setInputFieldValue = () => {
    const getCookieValue = getCookiesByName('_msuuid_');
    // scrip loaded in iframes
    const iframs = document.getElementsByClassName('hs-form-iframe');
    // script loaded in div
    const hbspt_forms = document.getElementsByClassName('hbspt-form');
    if (hbspt_forms.length > 0) {
      for (let index = 0; index < hbspt_forms.length; index++) {
        const hiddenInput = hbspt_forms[index] ? hbspt_forms[index].querySelector('[name="marin_uuid"]') : null;
        if (hbspt_forms[index]) {
          const cookieDump = hbspt_forms[index].querySelector('[name="cookie_dump"]');
          cookieDump
            ? (cookieDump.value = JSON.stringify({ ...getCookies(), isMarinFieldExist: !isEmpty(hiddenInput?.value) }))
            : null;
        }
        if (hiddenInput) {
          hiddenInput.value = getCookieValue;
        }
      }
    }
    if (iframs.length > 0) {
      for (let index = 0; index < iframs.length; index++) {
        const hiddenInput = iframs[index]
          ? iframs[index].contentWindow.document.body
              .getElementsByClassName('hbspt-form')[0]
              ?.querySelector('[name="marin_uuid"]')
          : null;
        if (iframs[index]) {
          const cookieDump = iframs[index].contentWindow.document.body
            .getElementsByClassName('hbspt-form')[0]
            ?.querySelector('[name="cookie_dump"]');
          cookieDump
            ? (cookieDump.value = JSON.stringify({ ...getCookies(), isMarinFieldExist: !isEmpty(hiddenInput?.value) }))
            : null;
        }
        if (hiddenInput) {
          hiddenInput.value = getCookieValue;
        }
      }
    }
  };

  setInputFieldValue();
};


export const ModalRawHTML = ({ html, isModal = false, loadDefer , ...props }: Props) => {
  const [headElements, setHeadElements] = useState<ReactElement[]>([]);
  const [bodyElements, setBodyElements] = useState<ReactElement[]>([]);

  useEffect(() => {
    if (!html) return;

    const scriptsWithSource: ReactElement[] = [];
    const bodyElements: ReactElement[] = [];
    /**
     * Use DOMParser to parse the html text into a Document at runtime.
     * We can then iterate over the document's <head> and <body> tags,
     * collecting all childNodes.
     *
     * DOMParser support: https://caniuse.com/mdn-api_domparser
     */
    const parser = new DOMParser();
    const parsed = parser.parseFromString(html, 'text/html');
    const nodes = Array.from(parsed.head.childNodes).concat(Array.from(parsed.body.childNodes));

    /**
     * Iterate over every node, and assign them to either
     * `scriptsWithSource` or `bodyElements`.
     * Scripts that have a src tag will be placed in the `head` with
     * the help of Helmet, and the rest will be inserted in the body
     */
    nodes.forEach((node, i) => {
      const ast = fromDom(node);
      if (ast.type !== 'element') return null;
      if (ast.tagName === 'script' && ast.properties?.src) {
        scriptsWithSource.push({ ...toH(React.createElement, ast), key: `head-element-${i}` });
      } else {
        bodyElements.push({ ...toH(React.createElement, ast), key: `body-element-${i}` });
      }
    });

    setHeadElements(scriptsWithSource);
    setBodyElements(bodyElements);
  }, [html, setHeadElements, setBodyElements]);

  useEffect(() => {
    const hubspotEvent = (event) => {
      if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormReady') {
        postScript();
      }
    };

    window.addEventListener('message', hubspotEvent);

    return () => window.removeEventListener('message', hubspotEvent);
  }, []);

  useEffect(() => {
    headElements.forEach((headScript) => {
      const script = document.createElement('script');
      script.src = headScript.props.src;
      script.async = false;
      if (loadDefer) {
      script.setAttribute('defer', '');
      }
      document.head.appendChild(script);
    });
  }, [headElements]);

  /**
   * HTML5 spec specifies that scripts inserted using innerHTML do not
   * execute when inserted: https://www.w3.org/TR/2008/WD-html5-20080610/dom.html#innerhtml0
   * Our body elements may contain scripts, so we still need to force the
   * browser to execute those. For that, we will use `useDangerousRawHtml`
   */
  const ref = useCallbackRef<HTMLDivElement>();
  useDangerousRawHtml(ref.current, isModal);

  if (!html || !bodyElements?.length) return null;
  return (
    <div {...props} ref={ref.callback}>
      {/* the head elements are loaded using the basic js,
        since react helmet has a known bug
        https://github.com/nfl/react-helmet/issues/419
      */}
      {bodyElements}
    </div>
  );
};
